import { StatCard } from '@/components/StatCard';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n/LanguageContext';

/**
 * "What needs attention right now" (Part B) - deliberately NOT a vanity
 * chart dashboard. Every number here links straight to the filtered list
 * that explains it. There is no cut-off/SLA engine in this codebase
 * (out of scope, Part S) so nothing here fabricates an urgency score -
 * `age_seconds` from GET /problems is exposed by the API and rendered as a
 * plain age, ready for a future severity prop without a rewrite.
 */
export function OverviewPage(): JSX.Element {
  const t = useT();
  const problems = useApi(() => endpoints.problems(), []);

  async function total(fn: () => Promise<{ total: number }>): Promise<number> {
    return (await fn()).total;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('overview.title')}</h1>
        <span className="sub">{t('overview.subtitle')}</span>
      </div>

      <div className="grid-cards">
        <StatCard label={t('overview.openInbounds')} href="/inbounds?openOnly=true"
          fetchCount={() => total(() => endpoints.inbounds({ openOnly: true, limit: 1 }))} />
        <StatCard
          label={t('overview.ordersToPick')}
          href="/shipments?status=RESERVED"
          fetchCount={() => total(() => endpoints.orders({ status: 'RESERVED', limit: 1 }))}
        />
        <StatCard
          label={t('overview.ordersToPack')}
          href="/shipments?status=PICKED"
          fetchCount={() => total(() => endpoints.orders({ status: 'PICKED', limit: 1 }))}
        />
        <StatCard
          label={t('overview.readyToShip')}
          href="/shipments"
          fetchCount={() => total(() => endpoints.shipments({ status: 'READY', limit: 1 }))}
        />
        <StatCard
          label={t('overview.returnsAwaitingDisposition')}
          href="/returns?awaitingDispositionOnly=true"
          fetchCount={() => total(() => endpoints.returns({ awaitingDispositionOnly: true, limit: 1 }))}
        />
        <StatCard
          label={t('overview.importConflicts')}
          href="/problems?importConflicts=true"
          fetchCount={() => total(() => endpoints.problems({ importConflicts: true }))}
        />
        <StatCard
          label={t('overview.billingExceptions')}
          href="/problems?unpriced=true"
          fetchCount={() => total(() => endpoints.problems({ unpriced: true }))}
        />
        <StatCard
          label={t('overview.problemOperations')}
          href="/problems?hasDiscrepancy=true"
          fetchCount={() => total(() => endpoints.problems({ hasDiscrepancy: true }))}
        />
      </div>

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('overview.needsAttention')}</h2>
        {problems.status === 'loading' && <LoadingState />}
        {problems.status === 'error' && <ErrorBanner error={problems.error} onRetry={problems.reload} />}
        {problems.status === 'success' &&
          (problems.data.rows.length === 0 ? (
            <EmptyState title={t('overview.nothingNeedsAttention')} />
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('overview.colType')}</th>
                  <th>{t('overview.colEntity')}</th>
                  <th>{t('overview.colSeller')}</th>
                  <th>{t('overview.colSeverity')}</th>
                  <th>{t('overview.colAge')}</th>
                </tr>
              </thead>
              <tbody>
                {problems.data.rows.slice(0, 20).map((row) => (
                  <tr key={`${row.type}-${row.entity_id}`} onClick={() => (window.location.href = hrefFor(row.type, row.entity_id))}>
                    <td>
                      <StatusBadge value={row.type} />
                    </td>
                    <td>
                      <Link to={hrefFor(row.type, row.entity_id)}>{row.entity_label}</Link>
                    </td>
                    <td>{row.seller_name ?? '—'}</td>
                    <td>{row.severity_label}</td>
                    <td>{formatAge(Number(row.age_seconds), t)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          ))}
      </div>
    </div>
  );
}

function hrefFor(type: string, entityId: string): string {
  switch (type) {
    case 'RECEIVING_DISCREPANCY':
      return `/inbounds`;
    case 'RETURN_AWAITING_DISPOSITION':
      return `/returns/${entityId}`;
    case 'IMPORT_CONFLICT':
      return `/imports/${entityId}`;
    case 'BILLING_UNPRICED':
      return `/billing`;
    case 'SHIPMENT_INCOMPLETE':
      return `/shipments/doc/${entityId}`;
    default:
      return '/problems';
  }
}

function formatAge(seconds: number, t: ReturnType<typeof useT>): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86400);
  if (days > 0) return t('overview.ageDays', { n: days });
  const hours = Math.floor(seconds / 3600);
  if (hours > 0) return t('overview.ageHours', { n: hours });
  return t('overview.ageMinutes', { n: Math.max(1, Math.floor(seconds / 60)) });
}
